/**
 * Theme runtime — converts the schema's semantic theme tokens (+ the frozen design
 * system's engine-read attributes) into a semantic {@link RuntimeTheme}. No CSS, no
 * pixels: the mapping to concrete styles is a Phase 9 component concern. Broken/unknown
 * design systems fall back to `modern`. See THEME_RUNTIME.md.
 */
import { DESIGN_SYSTEMS } from '../../registry'
import type { Theme, Branding } from '../../schema'
import type { RuntimeTheme } from '../types'

/**
 * Resolve the schema theme + branding into a semantic runtime theme.
 *
 * `theme.primaryColor` and `branding.brandColor` are two INDEPENDENT fields with distinct
 * ownership — neither is derived from, nor falls back to, the other:
 *   - `primaryColor` drives the site's visual chrome (navbar/footer/buttons/links/active
 *     states, via `--sb-primary`) and is the AI's own color selection, editable manually.
 *   - `brandColor` is business-identity metadata (`--sb-brand`), defaulting to `#000000`,
 *     never AI-generated, editable manually, with no bearing on `primaryColor`.
 * Passed straight through — no merge, no precedence, no artificial sync.
 */
export function resolveTheme(theme: Theme, branding: Branding): RuntimeTheme {
  const ds = DESIGN_SYSTEMS[theme.designSystem] ?? DESIGN_SYSTEMS.modern
  return {
    designSystem: theme.designSystem,
    primaryColor: theme.primaryColor,
    brandColor: branding.brandColor,
    typography: { headingFont: theme.typography.headingFont, bodyFont: theme.typography.bodyFont },
    borderRadius: theme.borderRadius,
    spacing: theme.spacing,
    navigationStyle: ds.navigationStyle,
    buttonStyle: ds.buttonStyle,
    visualTone: ds.visualTone,
  }
}
